import { Box, FormControl, InputLabel, FormHelperText } from '@mui/material';
import { clsx } from 'clsx';
import castArray from 'lodash/castArray';
import React, { Fragment } from 'react';
import { useInFormGrid } from '../../cmsPage/components/FormGrid';
import Spinner from '../../spinner/components/Spinner';
import cnMerge from '../../ui/primitives/cn';
import { fieldHelpClasses, fieldLabelClasses, fieldSlotClasses } from '../../ui/primitives/input';
import useFieldProps from '../hooks/useFieldProps';
import FormFieldError from './FormFieldError';

/**
 * The variants that keep the look they asked for. The shared fields are outlined by
 * default now (redesign iteration 1); a call site that passes `standard` or `filled` keeps
 * its floating label and its underline, exactly as before.
 */
export const isLegacyVariant = (variant) => variant === 'standard' || variant === 'filled';

/**
 * The one required marker: a red asterisk straight after the label. The fields that hand
 * their label to MUI switch MUI's own off (`InputLabelProps.required: false`), which was a
 * second marker (" *", after a thin space) and read "Event Name* *" beside this one.
 */
export const RequiredMark = () => <span className="required-annotation tw-text-error-text">*</span>;

/**
 * The wrapper every shared field sits in: its label, its control, and its help text or
 * error underneath.
 *
 * The label is static above the control (13/600 --ink-2, 6px over it), the help text and
 * the error share one slot 6px under it, and the error carries its circle-alert. A call
 * site on the legacy variants gets the old markup instead, unchanged.
 *
 * Optional props, all new: `help` (the help text), `span="full"` (the field takes a whole
 * row of a FormGrid), and three ids a field passes so its control can point at its label
 * and its message: `labelFor`, `labelId` and `errorId`. Inside a FormGrid the field fills
 * its column and leaves the vertical rhythm to the grid's gap.
 *
 * `labelAside` is drawn on the label's line, 4px after it and outside the `<label>`: a
 * HelpPopover "?" put inside the label became part of the field's name ("Name Help *").
 * It needs the label this wrapper draws, so the legacy floating label does not take it.
 */
const FieldWrapper = (props) => {
  const {
    label,
    className: providedClassName,
    cn,
    children,
    showLabel,
    showError,
    fontWeight,
    showSpinner = false,
    controlProps,
    type,
    html,
    htmlUnder,
    variant,
    help,
    span,
    labelFor,
    labelId,
    errorId,
    labelAside,
  } = props;

  const showControl = !!controlProps;
  const inGrid = useInFormGrid();
  const legacy = isLegacyVariant(variant);

  const { isRequired, width, fieldError, hasError } = useFieldProps(props);

  const Base = showControl ? FormControl : Box;
  const ErrorWrapper = showControl ? FormHelperText : Fragment;

  const margins = inGrid
    ? { my: 0 }
    : html
    ? htmlUnder
      ? { mb: 0, mt: '1rem' }
      : { mt: 0, mb: '1rem' }
    : { my: '1rem' };

  const legacyLabel =
    type === 'select' ? (
      <InputLabel style={{ fontWeight, left: -15 }}>
        {label}
        {isRequired && <span className="required-annotation">*</span>}
      </InputLabel>
    ) : (
      <InputLabel style={{ fontWeight }}>
        {label}
        {isRequired && <span className="required-annotation">*</span>}
      </InputLabel>
    );

  // An empty label drew an empty box with a margin under it; there is nothing to say.
  const labelText = (label || isRequired) && (
    <label
      htmlFor={labelFor}
      id={labelId}
      className={cnMerge('field-label', fieldLabelClasses, labelAside && 'tw-mb-0')}
    >
      {label}
      {isRequired && <RequiredMark />}
    </label>
  );
  // The aside shares the label's line and takes over its gap to the box.
  const fieldLabel =
    labelText && labelAside ? (
      <div className="field-label-row tw-mb-label tw-flex tw-items-center tw-gap-1">
        {labelText}
        {labelAside}
      </div>
    ) : (
      labelText
    );

  return (
    <Box className={span === 'full' ? 'form-grid-full' : undefined}>
      <Base
        className={clsx(
          providedClassName,
          { 'has-error': !!fieldError },
          ...(cn ? castArray(cn) : [])
        )}
        sx={{ width: inGrid ? '100%' : width, ...margins }}
        {...(showControl && { ...controlProps, error: hasError })}
      >
        {showSpinner ? (
          <Box>
            <Spinner />
          </Box>
        ) : (
          <>
            {showLabel && (legacy ? legacyLabel : fieldLabel)}
            {children}
            {showError &&
              (legacy ? (
                <ErrorWrapper>
                  <FormFieldError error={fieldError} />
                </ErrorWrapper>
              ) : (
                <FormFieldError error={fieldError} id={errorId} className={fieldSlotClasses} />
              ))}
            {/* The same slot, so the control's aria-describedby names whichever is showing. */}
            {help && !(showError && fieldError) && (
              <span className={clsx('field-help', fieldHelpClasses, fieldSlotClasses)} id={errorId}>
                {help}
              </span>
            )}
          </>
        )}
      </Base>
    </Box>
  );
};

export default FieldWrapper;
